import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import Toolbar from '@mui/material/Toolbar';
import {
  DataGrid,
  GridActionsCellItem,
  GridEventListener,
  GridColDef,
  GridRowId,
  GridRowModesModel,
  selectedGridRowsSelector,
  gridFilteredSortedRowIdsSelector,
  GridToolbar,
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import '../App.css';
import {
  fetchCommissionSales, deleteReferrals, Referral,
} from '../features/referrals/refarrals';
import { RootState } from '../app/store';

const CommissionSales = () => {
  const referralState = useAppSelector((state: RootState) => state.referrals);
  const dispatch = useAppDispatch();
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rows, setRows] = useState(referralState.value);

  useEffect(() => { dispatch(fetchCommissionSales()); }, []);
  useEffect(() => setRows(referralState.value), [referralState.value]);

  const handleDeleteClick = (id: GridRowId) => () => {
    const rowToDelete = rows.find((row) => row.cb_id === id);
    if (rowToDelete) {
      setRows(rows.filter((row) => row.cb_id !== id));
      dispatch(deleteReferrals(rowToDelete));
    }
  };

  const columns: GridColDef<Referral>[] = [
    {
      field: 'referrer_email',
      headerName: 'Email',
      editable: false,
      width: 160,
    },
    {
      field: 'referral_code',
      headerName: 'Referral Code',
      editable: false,
      width: 100,
    },
    {
      field: 'first_name',
      headerName: 'First Name',
      editable: false,
      width: 120,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      editable: false,
      width: 120,
    },
    {
      field: 'redeemed',
      headerName: 'Redeemed',
      editable: false,
      width: 80,
      valueGetter: ((params) => referralState.value.filter(
        (referral) => referral.referral_code === params.row.referral_code && referral.redeemed,
      ).length),
    },
    {
      field: 'pending',
      headerName: 'Pending',
      editable: false,
      width: 80,
      valueGetter: ((params) => referralState.value.filter(
        (referral) => (referral.referral_code === params.row.referral_code)
        && !referral.redeemed
        && (referral.referral_code !== referral.sk)
        && (referral.referrer_email !== referral.sk),
      ).length),
    },
    {
      headerName: 'Delete',
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDeleteClick(params.row.cb_id)}
        />,
      ],
    },
  ];

  const navigate = useNavigate();

  const handleCellClick: GridEventListener<'cellClick'> = (params) => {
    if (params.colDef.headerName === 'Pending') {
      navigate(`referral_report/${params.row.referral_code}?showPending=true`);
    } else if (params.colDef.headerName === 'Redeemed') {
      navigate(`referral_report/${params.row.referral_code}`);
    }
  };

  const getSelectedRowsToExport = ({
    apiRef,
  }: any): GridRowId[] => {
    const selectedRowIds = selectedGridRowsSelector(apiRef);
    if (selectedRowIds.size > 0) {
      return Array.from(selectedRowIds.keys());
    }

    return gridFilteredSortedRowIdsSelector(apiRef);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, mr: 15, ml: 15, mt: 10,
      }}
    >
      <Toolbar disableGutters sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Commission Sales</Typography>
        <Button href="/dashboard/commission_sales/add_sales_person" startIcon={<AddIcon />} variant="contained">
          Add Salesperson
        </Button>
      </Toolbar>
      <DataGrid
        onCellClick={handleCellClick}
        getRowId={(row) => row.cb_id}
        autoHeight
        loading={referralState.status === 'loading'}
        rows={rows.filter((row) => row.sk === row.referral_code)}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: { printOptions: { getRowsToExport: getSelectedRowsToExport } },
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'speed', sort: 'asc' }],
          },
        }}
        pageSizeOptions={[100]}
        componentsProps={{
          toolbar: { setRows, setRowModesModel },
        }}
      />
    </Paper>
  );
};

export default CommissionSales;
